import { TransportBase, type Transport, type TransportStatus } from './types.ts';

// Wi-Fi adapters (OBDLink MX Wi-Fi and every ELM327 clone) expose a raw TCP
// socket. A browser cannot open one, so this transport is a WebSocket to the
// local bridge, which owns the socket. Binary frames are adapter bytes; text
// frames are bridge status JSON. Nothing else uses text frames, so the two
// never collide.

export interface WifiTransportOptions {
  host?: string;
  port?: number;
  /** Bridge origin. Defaults to same-origin, which the Vite proxy handles. */
  bridgeBase?: string;
}

export class WifiTransport extends TransportBase implements Transport {
  readonly kind = 'wifi' as const;
  readonly label: string;

  private ws: WebSocket | null = null;
  private opts: WifiTransportOptions;

  constructor(opts: WifiTransportOptions = {}) {
    super();
    this.opts = opts;
    this.label = `Wi-Fi ${opts.host ?? '192.168.0.10'}:${opts.port ?? 35000}`;
  }

  get isOpen() {
    return this.ws?.readyState === WebSocket.OPEN && this.status.state === 'open';
  }

  private url(): string {
    const base = this.opts.bridgeBase ?? window.location.origin;
    const url = new URL('/obd', base);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    if (this.opts.host) url.searchParams.set('host', this.opts.host);
    if (this.opts.port) url.searchParams.set('port', String(this.opts.port));
    return url.toString();
  }

  async open(): Promise<void> {
    if (this.ws) await this.close();
    this.emitStatus('connecting', `Opening ${this.label}`);

    return new Promise((resolve, reject) => {
      let settled = false;
      const ws = new WebSocket(this.url());
      ws.binaryType = 'arraybuffer';
      this.ws = ws;

      // The socket to the bridge opening is not the same as the bridge reaching
      // the adapter. Only the bridge's own 'open' status means we can talk OBD.
      ws.onmessage = (ev) => {
        if (typeof ev.data === 'string') {
          let msg: TransportStatus & { type?: string; state?: string; detail?: string; target?: string };
          try {
            msg = JSON.parse(ev.data);
          } catch {
            return;
          }
          if (msg.type !== 'status') return;

          if (msg.state === 'open') {
            this.emitStatus('open', `Connected to ${msg.target ?? this.label}`);
            if (!settled) {
              settled = true;
              resolve();
            }
          } else if (msg.state === 'error') {
            this.emitStatus('error', msg.detail);
            if (!settled) {
              settled = true;
              reject(new Error(msg.detail ?? 'Bridge could not reach the adapter.'));
            }
          } else if (msg.state === 'closed') {
            this.emitStatus('closed', msg.detail);
          } else if (msg.state === 'connecting') {
            this.emitStatus('connecting', `Bridge dialling ${msg.target ?? ''}`);
          }
          return;
        }
        this.emitData(new Uint8Array(ev.data as ArrayBuffer));
      };

      ws.onerror = () => {
        this.emitStatus('error', 'Could not reach the bridge. Is `npm run dev` still running?');
        if (!settled) {
          settled = true;
          reject(new Error('Bridge unreachable on ' + this.url()));
        }
      };

      ws.onclose = () => {
        this.ws = null;
        if (this.status.state !== 'error') this.emitStatus('closed');
        if (!settled) {
          settled = true;
          reject(new Error('Bridge closed the connection before the adapter answered.'));
        }
      };
    });
  }

  async close(): Promise<void> {
    const ws = this.ws;
    this.ws = null;
    if (!ws) return;
    this.emitStatus('closing');
    try {
      ws.close();
    } catch {
      // already gone
    }
    this.emitStatus('closed');
  }

  async write(bytes: Uint8Array): Promise<void> {
    if (this.ws?.readyState !== WebSocket.OPEN) throw new Error('Wi-Fi transport is not open.');
    // Copy into a standalone buffer; the caller may reuse theirs.
    this.ws.send(bytes.slice().buffer);
  }
}
